import { useAudioTranscription } from "@/api/audio-transcription";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import {
  DictationCancelledError,
  LIVE_MAX_RECORDING_DURATION,
  useLiveTranscription,
} from "@/hooks/useLiveTranscription";
import { micCaptureIsSupported } from "@/hooks/voice/micCapture";
import { Flags } from "@/providers/LaunchDarklyProvider";
import createDebug from "debug";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useRef, useState } from "react";

const debug = createDebug("cohesive:dictation");

const LEGACY_MAX_RECORDING_DURATION = 120;
/** Safety net for a hung legacy upload/transcribe roundtrip. */
const LEGACY_TRANSCRIPTION_TIMEOUT_MS = 30_000;

interface UseDictationOptions {
  /** Receives the final transcript. May be empty — callers decide how to
   * surface that. Fires identically from both engines. */
  onTranscriptionComplete?: (text: string) => void;
  /** Receives user-ready error messages (toast `error.message` directly). */
  onError?: (error: Error) => void;
  onMaxDurationReached?: () => void;
}

interface UseDictationReturn {
  isRecording: boolean;
  isSupported: boolean;
  audioLevel: number;
  error: string | null;
  duration: number;
  /** Live transcript while speaking; always "" on the legacy engine. */
  interimText: string;
  /** The stop → final-text gap (live) or upload+transcribe (legacy). */
  isFinalizing: boolean;
  /** Cap for the engine in (or about to be in) use — drive timer UI off this. */
  maxDurationSeconds: number;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
}

/**
 * Facade over the two dictation engines: live streaming transcription over
 * the realtime bridge (behind the `liveTranscription` flag) and the legacy
 * record-blob →
 * `POST audio-transcription` pipeline. Engine choice happens at
 * startRecording(): live is tried first when eligible, and a socket
 * connect/ready failure silently falls back to legacy before the mic is
 * touched. Mic errors after connect do not fall back — the mic would fail the
 * same way on either engine.
 */
export function useDictation(
  options: UseDictationOptions = {},
): UseDictationReturn {
  const flags = useFlags<Flags>();
  const engineRef = useRef<"live" | "legacy" | null>(null);
  const legacyCancelledRef = useRef(false);
  const legacyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLegacyFinalizing, setIsLegacyFinalizing] = useState(false);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const liveEligible =
    flags.liveTranscription === true &&
    typeof WebSocket !== "undefined" &&
    micCaptureIsSupported();

  const live = useLiveTranscription({
    onTranscriptionComplete: (text) => {
      engineRef.current = null;
      optionsRef.current.onTranscriptionComplete?.(text);
    },
    onError: (error) => {
      engineRef.current = null;
      optionsRef.current.onError?.(error);
    },
    onMaxDurationReached: () => optionsRef.current.onMaxDurationReached?.(),
  });

  const clearLegacyFinalizing = useCallback(() => {
    setIsLegacyFinalizing(false);
    if (legacyTimeoutRef.current) {
      clearTimeout(legacyTimeoutRef.current);
      legacyTimeoutRef.current = null;
    }
  }, []);

  const transcriptionMutation = useAudioTranscription({
    onSuccess: (data) => {
      clearLegacyFinalizing();
      engineRef.current = null;
      optionsRef.current.onTranscriptionComplete?.(data.text ?? "");
    },
    onError: (error) => {
      clearLegacyFinalizing();
      engineRef.current = null;
      debug("legacy transcription error", error);
      optionsRef.current.onError?.(
        new Error(
          error instanceof Error
            ? `Transcription failed: ${error.message}`
            : "Failed to transcribe audio.",
        ),
      );
    },
  });

  const handleLegacyRecordingComplete = useCallback(
    (audioBlob: Blob) => {
      if (legacyCancelledRef.current) {
        legacyCancelledRef.current = false;
        engineRef.current = null;
        return;
      }
      setIsLegacyFinalizing(true);
      legacyTimeoutRef.current = setTimeout(() => {
        debug("legacy transcription timed out");
        clearLegacyFinalizing();
        engineRef.current = null;
        optionsRef.current.onError?.(
          new Error("Transcription timed out. Please try again."),
        );
      }, LEGACY_TRANSCRIPTION_TIMEOUT_MS);
      transcriptionMutation.mutate({
        audioBlob,
        options: {
          model: "gpt-4o-transcribe",
          response_format: "json",
        },
      });
    },
    [clearLegacyFinalizing, transcriptionMutation],
  );

  const legacy = useAudioRecording({
    onRecordingComplete: handleLegacyRecordingComplete,
    onError: (error) => {
      clearLegacyFinalizing();
      engineRef.current = null;
      optionsRef.current.onError?.(
        new Error(`Recording failed: ${error.message}`),
      );
    },
    onMaxDurationReached: () => optionsRef.current.onMaxDurationReached?.(),
  });

  const startRecording = useCallback(async () => {
    if (engineRef.current !== null) {
      return;
    }
    if (liveEligible) {
      engineRef.current = "live";
      try {
        await live.startRecording();
        return;
      } catch (connectError) {
        if (connectError instanceof DictationCancelledError) {
          // The user cancelled (or the surface unmounted) mid-connect;
          // starting the legacy recorder would grab the mic for a session
          // they already dismissed. cancelRecording already released the
          // engine claim — and a restarted attempt may hold it again, so
          // this stale attempt must not touch engineRef.
          debug("live connect cancelled, not falling back");
          return;
        }
        // A genuine connect failure cannot race a restart: the engine claim
        // above blocked re-entry for the whole await.
        engineRef.current = null;
        debug("live engine unavailable, falling back to legacy", connectError);
      }
    }
    engineRef.current = "legacy";
    legacyCancelledRef.current = false;
    await legacy.startRecording();
  }, [liveEligible, live, legacy]);

  const stopRecording = useCallback(() => {
    if (engineRef.current === "live") {
      live.stopRecording();
    } else {
      legacy.stopRecording();
    }
  }, [live, legacy]);

  const cancelRecording = useCallback(() => {
    if (engineRef.current === "live") {
      live.cancelRecording();
      engineRef.current = null;
    } else if (legacy.isRecording) {
      // A recorder is live: exactly one blob is still owed, and consuming
      // the cancel flag on that blob is what releases the engine claim
      // (handleLegacyRecordingComplete). Releasing earlier would let a
      // restart reset the flag before the doomed blob lands.
      legacyCancelledRef.current = true;
      clearLegacyFinalizing();
      legacy.stopRecording();
    } else {
      // No recorder yet (start still in flight, or already settled): no
      // blob is coming. stopRecording aborts a pending start inside the
      // hook; release the claim here so a restart can proceed.
      clearLegacyFinalizing();
      legacy.stopRecording();
      legacyCancelledRef.current = false;
      engineRef.current = null;
    }
  }, [live, legacy, clearLegacyFinalizing]);

  const activeEngine = engineRef.current ?? (liveEligible ? "live" : "legacy");
  const usingLive = activeEngine === "live";

  return {
    isRecording: usingLive ? live.isRecording : legacy.isRecording,
    isSupported: legacy.isSupported || liveEligible,
    audioLevel: usingLive ? live.audioLevel : legacy.audioLevel,
    error: usingLive ? live.error : legacy.error,
    duration: usingLive ? live.duration : legacy.duration,
    interimText: usingLive ? live.interimText : "",
    isFinalizing: usingLive ? live.isFinalizing : isLegacyFinalizing,
    maxDurationSeconds: usingLive
      ? LIVE_MAX_RECORDING_DURATION
      : LEGACY_MAX_RECORDING_DURATION,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
